import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { NATIVE_INVOICE_PAGE_SIZE_PX } from "@common/helpers/nativeInvoicePagination";
import type { NativeInvoicePageSize } from "@common/constants/nativeInvoice";
import { VStack } from "@design-system/VStack/VStack";
import { writeNativeInvoicePreviewHtml } from "./NativeInvoiceDrawer/writeNativeInvoicePreviewHtml";
import { InvoicePreviewPager } from "./NativeInvoiceDrawer/InvoicePreviewPager";
import { useInvoicePreviewPaperScale } from "./NativeInvoiceDrawer/useInvoicePreviewPaperScale";
import frameStyles from "./invoicePaperFrame.module.scss";
import styles from "./InvoicePreviewPage.module.scss";

interface Props {
    // Fully rendered document HTML from the preview endpoint (same HTML the
    // PDF is generated from server-side). Rendering pre-built HTML here
    // (instead of re-deriving it from the render snapshot) keeps this page a
    // thin, read-only viewer with no form-state plumbing.
    html: string;
    pageSize: NativeInvoicePageSize;
    // "fit" scales one page into the viewport and steps with the pager.
    // "scroll" fits each page to the width and stacks them as separate sheets.
    layout: "fit" | "scroll";
}

function countDocumentPages(html: string): number {
    return Math.max(html.match(/class="page"/g)?.length ?? 1, 1);
}

interface ScrollSheetsProps {
    html: string;
    pageWidth: number;
    pageHeight: number;
    paperScale: number;
}

const InvoicePreviewScrollSheets: React.FC<ScrollSheetsProps> = ({
    html,
    pageWidth,
    pageHeight,
    paperScale,
}) => {
    const frameRefs = useRef<Array<HTMLIFrameElement | null>>([]);
    const pageCount = useMemo(() => countDocumentPages(html), [html]);
    const pageIndexes = useMemo(
        () => Array.from({ length: pageCount }, (_, index) => index),
        [pageCount],
    );

    useLayoutEffect(() => {
        pageIndexes.forEach((index) => {
            const iframe = frameRefs.current[index];
            if (!iframe) {
                return;
            }
            writeNativeInvoicePreviewHtml(iframe, html, index);
        });
    }, [html, pageIndexes]);

    return (
        <VStack gap={24} align="center" className={styles.pageList}>
            {pageIndexes.map((index) => (
                <div
                    key={index}
                    className={styles.pageSheet}
                    style={{ zoom: paperScale }}
                >
                    <div className={frameStyles.paperChrome}>
                        <iframe
                            ref={(node) => {
                                frameRefs.current[index] = node;
                            }}
                            className={frameStyles.documentFrame}
                            title={
                                pageCount > 1
                                    ? `Invoice preview, page ${index + 1}`
                                    : "Invoice preview"
                            }
                            sandbox="allow-same-origin"
                            style={{
                                width: pageWidth,
                                height: pageHeight,
                            }}
                        />
                    </div>
                </div>
            ))}
        </VStack>
    );
};

// Standalone, read-only paginated document viewer for the invoice preview
// page. Unlike InvoiceDocumentPreview (which drives the live edit form), this
// only ever displays already-rendered HTML and derives its page count by
// inspecting the written iframe DOM.
export const InvoicePreviewDocument: React.FC<Props> = ({
    html,
    pageSize,
    layout,
}) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageCount, setPageCount] = useState(1);

    const logicalPaper = NATIVE_INVOICE_PAGE_SIZE_PX[pageSize];
    const isScrollLayout = layout === "scroll";
    const showPager = !isScrollLayout && pageCount > 1;
    const paperScale = useInvoicePreviewPaperScale({
        viewportRef,
        logicalPaper,
        showPager,
        fitToViewportHeight: !isScrollLayout,
    });

    useLayoutEffect(
        function syncPreviewIframe() {
            if (isScrollLayout) {
                return;
            }
            const iframe = iframeRef.current;
            if (!iframe) {
                return;
            }
            writeNativeInvoicePreviewHtml(iframe, html, pageIndex);
            const count =
                iframe.contentDocument?.querySelectorAll(".page").length ?? 1;
            setPageCount(Math.max(count, 1));
        },
        [html, isScrollLayout, pageIndex],
    );

    return (
        <div
            ref={viewportRef}
            className={classNames(styles.viewport, {
                [styles.viewportScrollable]: isScrollLayout,
            })}
        >
            {isScrollLayout ? (
                <InvoicePreviewScrollSheets
                    html={html}
                    pageWidth={logicalPaper.width}
                    pageHeight={logicalPaper.height}
                    paperScale={paperScale}
                />
            ) : (
                <div className={styles.paperStack} style={{ zoom: paperScale }}>
                    <div
                        className={classNames(frameStyles.paperChrome, {
                            [frameStyles.paperChromeWithPager]: showPager,
                        })}
                    >
                        <iframe
                            ref={iframeRef}
                            className={frameStyles.documentFrame}
                            title="Invoice preview"
                            sandbox="allow-same-origin"
                            style={{
                                width: logicalPaper.width,
                                height: logicalPaper.height,
                            }}
                        />
                    </div>
                    {showPager ? (
                        <InvoicePreviewPager
                            pageCount={pageCount}
                            pageIndex={pageIndex}
                            onPageChange={setPageIndex}
                        />
                    ) : null}
                </div>
            )}
        </div>
    );
};
